import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StickySaveBar from "@/components/admin/StickySaveBar";
import { toast } from "sonner";
import { Users, Settings2, RotateCcw, Trophy, Save, Edit3, Trash2 } from "lucide-react";

// =============================================================================
// TOP Vs Humano — Admin page (mirrors VsRobotUsers.jsx). Controls the public
// leaderboard configuration for real-time PvP duels: title/subtitle per
// language, public size, reset periodicity, manual reset, history wipe and
// per-player rename.
// =============================================================================

const DOW_LABELS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
const PERIOD_LABELS = { weekly: "Semanal", daily: "Diário", monthly: "Mensal", never: "Nunca (sem reset automático)" };

export default function AdminVsHumanUsers() {
  const [cfg, setCfg] = useState(null);
  const [board, setBoard] = useState([]);
  const [seasonView, setSeasonView] = useState("current"); // "current" | "all"
  const [loadingBoard, setLoadingBoard] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [editPlayer, setEditPlayer] = useState(null); // {old_name, new_name}

  const reload = async () => {
    try {
      const [c, b] = await Promise.all([
        api.get("/vs-human/admin/config"),
        api.get("/vs-human/admin/leaderboard", { params: { season: seasonView, limit: 5000 } }),
      ]);
      setCfg(c.data);
      setBoard(b.data || []);
    } catch (e) { toast.error("Falha ao carregar TOP"); }
  };
  useEffect(() => { reload(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  useEffect(() => {
    (async () => {
      setLoadingBoard(true);
      try {
        const b = await api.get("/vs-human/admin/leaderboard", { params: { season: seasonView, limit: 5000 } });
        setBoard(b.data || []);
      } finally { setLoadingBoard(false); }
    })();
  }, [seasonView]);

  const saveCfg = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const { data } = await api.put("/vs-human/admin/config", {
        title_pt: cfg.title_pt || "",
        title_en: cfg.title_en || "",
        title_es: cfg.title_es || "",
        subtitle_pt: cfg.subtitle_pt || "",
        subtitle_en: cfg.subtitle_en || "",
        subtitle_es: cfg.subtitle_es || "",
        public_limit: Number(cfg.public_limit) || 10,
        reset_period: cfg.reset_period,
        reset_dow: Number(cfg.reset_dow),
        reset_hour: Number(cfg.reset_hour),
        reset_minute: Number(cfg.reset_minute),
      });
      setCfg(data);
      toast.success("Configuração guardada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro a guardar");
    } finally { setSaving(false); }
  };

  const doReset = async () => {
    if (!window.confirm("Reset manual do TOP Vs Humano — confirmas? O histórico permanece guardado mas o ranking público começa novo.")) return;
    setResetting(true);
    try {
      await api.post("/vs-human/admin/reset");
      toast.success("Ranking reposto a zero");
      reload();
    } catch (e) { toast.error("Erro no reset"); }
    finally { setResetting(false); }
  };

  const doDeleteAllHistory = async () => {
    const yes1 = window.confirm(
      "⚠️ ATENÇÃO: Vais APAGAR PERMANENTEMENTE todos os duelos Vs Humano (all-time + corrente).\n\nIsto é IRREVERSÍVEL e não pode ser desfeito.\n\nQueres mesmo continuar?"
    );
    if (!yes1) return;
    const confirm = window.prompt('Para confirmar, escreve EXACTAMENTE: APAGAR HISTÓRICO');
    if (confirm !== "APAGAR HISTÓRICO") {
      toast.info("Operação cancelada.");
      return;
    }
    setResetting(true);
    try {
      const { data } = await api.delete("/vs-human/admin/history");
      toast.success(`Histórico apagado: ${data.deleted} duelos removidos.`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro a apagar histórico");
    } finally { setResetting(false); }
  };

  const savePlayer = async () => {
    if (!editPlayer || !editPlayer.old_name) return;
    if (!editPlayer.new_name) { toast.error("Indica um novo nome"); return; }
    try {
      await api.put("/vs-human/admin/player/rename", {
        old_name: editPlayer.old_name,
        new_name: editPlayer.new_name,
      });
      toast.success("Jogador atualizado");
      setEditPlayer(null);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro a atualizar");
    }
  };

  if (!cfg) {
    return <div className="text-center text-slate-500 py-12" data-testid="vsh-users-loading">A carregar configuração…</div>;
  }

  return (
    <div className="max-w-5xl mx-auto" data-testid="admin-vsh-users-page">
      <header className="mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-sky-500 via-indigo-500 to-violet-500 grid place-items-center text-white shadow-md">
            <Users className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-[Fredoka] font-bold text-slate-900" data-testid="vsh-users-page-title">Utilizadores VS Humano</h1>
            <p className="text-sm text-slate-500">Configura o TOP público e gere os jogadores que duelaram em tempo real.</p>
          </div>
        </div>
      </header>

      <div className="space-y-6">
        {/* Public aspect */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2"><Settings2 className="w-4 h-4" /> Aspeto público</h3>
          <p className="text-[11px] text-slate-500 mb-3">Define o título e subtítulo para cada idioma. Se um idioma ficar vazio, o público vê a versão em Português.</p>

          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Título</Label>
              <div className="grid sm:grid-cols-3 gap-2 mt-1">
                {[
                  { code: "pt", flag: "🇵🇹", label: "PT" },
                  { code: "en", flag: "🇬🇧", label: "EN" },
                  { code: "es", flag: "🇪🇸", label: "ES" },
                ].map(({ code, flag, label }) => (
                  <div key={`title-${code}`}>
                    <div className="text-[10px] text-slate-500 mb-1 flex items-center gap-1"><span>{flag}</span> {label}</div>
                    <Input
                      value={cfg[`title_${code}`] ?? ""}
                      onChange={(e) => setCfg({ ...cfg, [`title_${code}`]: e.target.value })}
                      data-testid={`vsh-top-title-${code}`}
                      placeholder={code !== "pt" ? "(usa PT se vazio)" : ""}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Subtítulo</Label>
              <div className="grid sm:grid-cols-3 gap-2 mt-1">
                {[
                  { code: "pt", flag: "🇵🇹", label: "PT" },
                  { code: "en", flag: "🇬🇧", label: "EN" },
                  { code: "es", flag: "🇪🇸", label: "ES" },
                ].map(({ code, flag, label }) => (
                  <div key={`subtitle-${code}`}>
                    <div className="text-[10px] text-slate-500 mb-1 flex items-center gap-1"><span>{flag}</span> {label}</div>
                    <Input
                      value={cfg[`subtitle_${code}`] ?? ""}
                      onChange={(e) => setCfg({ ...cfg, [`subtitle_${code}`]: e.target.value })}
                      data-testid={`vsh-top-subtitle-${code}`}
                      placeholder={code !== "pt" ? "(usa PT se vazio)" : ""}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="max-w-xs">
              <Label>Nº de jogadores no ranking público</Label>
              <Input type="number" min={1} max={500} value={cfg.public_limit ?? 10} onChange={(e) => setCfg({ ...cfg, public_limit: e.target.value })} data-testid="vsh-top-limit" />
            </div>
          </div>
        </section>

        {/* Reset config */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2"><RotateCcw className="w-4 h-4" /> Reset automático</h3>
          <div className="grid sm:grid-cols-4 gap-3">
            <div>
              <Label>Periodicidade</Label>
              <Select value={cfg.reset_period} onValueChange={(v) => setCfg({ ...cfg, reset_period: v })}>
                <SelectTrigger data-testid="vsh-top-period"><SelectValue /></SelectTrigger>
                <SelectContent className="z-[100]">
                  {Object.entries(PERIOD_LABELS).map(([k, lbl]) => <SelectItem key={k} value={k}>{lbl}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {cfg.reset_period === "weekly" && (
              <div>
                <Label>Dia da semana</Label>
                <Select value={String(cfg.reset_dow)} onValueChange={(v) => setCfg({ ...cfg, reset_dow: Number(v) })}>
                  <SelectTrigger data-testid="vsh-top-dow"><SelectValue /></SelectTrigger>
                  <SelectContent className="z-[100]">
                    {DOW_LABELS.map((lbl, i) => <SelectItem key={`dow-${i}-${lbl}`} value={String(i)}>{lbl}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Hora (UTC)</Label>
              <Input type="number" min={0} max={23} value={cfg.reset_hour ?? 0} onChange={(e) => setCfg({ ...cfg, reset_hour: e.target.value })} data-testid="vsh-top-hour" />
            </div>
            <div>
              <Label>Minuto</Label>
              <Input type="number" min={0} max={59} value={cfg.reset_minute ?? 0} onChange={(e) => setCfg({ ...cfg, reset_minute: e.target.value })} data-testid="vsh-top-minute" />
            </div>
          </div>
          <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
            <p className="text-xs text-slate-500">
              Última season iniciada: <b>{cfg.season_start_at ? new Date(cfg.season_start_at).toLocaleString("pt-PT") : "—"}</b>
              {cfg.last_reset_at && <span> · Último reset: {new Date(cfg.last_reset_at).toLocaleString("pt-PT")}</span>}
            </p>
            <Button onClick={doReset} disabled={resetting} variant="outline" className="rounded-full border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="vsh-top-reset">
              <RotateCcw className={`w-4 h-4 mr-1.5 ${resetting ? "animate-spin" : ""}`} /> Reset manual
            </Button>
            <Button onClick={doDeleteAllHistory} disabled={resetting} variant="outline" className="rounded-full border-red-400 text-red-700 hover:bg-red-50" data-testid="vsh-top-delete-history">
              <Trash2 className="w-4 h-4 mr-1.5" /> Eliminar histórico
            </Button>
          </div>
        </section>

        {/* Leaderboard */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2"><Trophy className="w-4 h-4" /> Leaderboard ({board.length} jogadores)</h3>
            <div className="flex gap-1.5">
              <button onClick={() => setSeasonView("current")} className={`px-3 py-1 rounded-full text-xs font-bold ${seasonView === "current" ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-700"}`} data-testid="vsh-top-view-current">Season atual</button>
              <button onClick={() => setSeasonView("all")} className={`px-3 py-1 rounded-full text-xs font-bold ${seasonView === "all" ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-700"}`} data-testid="vsh-top-view-all">All-time</button>
            </div>
          </div>
          {loadingBoard ? (
            <div className="text-center text-slate-400 py-4">A carregar…</div>
          ) : board.length === 0 ? (
            <div className="text-center text-slate-400 py-4 italic">Sem jogadores nesta vista.</div>
          ) : (
            <div className="border rounded-xl divide-y max-h-[480px] overflow-y-auto" data-testid="vsh-top-board">
              {board.map((row, i) => (
                <div key={`${row.player_name}-${i}`} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50" data-testid={`vsh-top-row-${i}`}>
                  <span className="w-7 text-xs font-bold text-slate-400">#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900 truncate">{row.player_name}</div>
                  </div>
                  <span className="text-xs font-bold text-violet-700 bg-violet-50 px-2 py-0.5 rounded-full">{Math.round((row.points || 0) * 10) / 10} pts</span>
                  <span className="text-[10px] text-slate-500 hidden sm:inline">{row.wins}V·{row.losses}D·{row.ties}E</span>
                  <button
                    onClick={() => setEditPlayer({ old_name: row.player_name, new_name: row.player_name })}
                    className="p-1 text-slate-400 hover:text-sky-600"
                    data-testid={`vsh-top-edit-${i}`}
                    title="Editar nome"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <StickySaveBar>
        <Button onClick={saveCfg} disabled={saving} className="bg-sky-500 hover:bg-sky-600 rounded-full text-white" data-testid="vsh-top-save">
          <Save className="w-4 h-4 mr-2" /> {saving ? "A guardar…" : "Guardar configuração"}
        </Button>
      </StickySaveBar>

      {editPlayer && (
        <div className="fixed inset-0 z-[90] bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setEditPlayer(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4" data-testid="vsh-top-edit-dialog">
            <h3 className="font-[Fredoka] text-lg font-bold text-slate-900">Editar jogador</h3>
            <div>
              <Label>Nome</Label>
              <Input value={editPlayer.new_name} onChange={(e) => setEditPlayer({ ...editPlayer, new_name: e.target.value })} data-testid="vsh-top-edit-name" />
            </div>
            <p className="text-[11px] text-slate-500">Esta alteração será aplicada a TODOS os duelos com o nome <b>{editPlayer.old_name}</b>.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditPlayer(null)}>Cancelar</Button>
              <Button onClick={savePlayer} className="bg-sky-500 hover:bg-sky-600 rounded-full" data-testid="vsh-top-edit-save">
                <Save className="w-4 h-4 mr-2" /> Guardar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
